import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { apiDownload, apiRequest } from "../api/client";
import { Button } from "../components/Button/Button";
import { formatMoney, quantityFromMilliunits } from "./format";
import type {
  GSTSupplyType,
  QuotationCustomerResponse,
  QuotationItem,
  QuotationStatus,
  QuotationTaxType,
} from "./types";
import "./PublicQuotationPage.css";

interface PublicQuotationEnvelope {
  quotation: {
    quotation_number: string;
    revision_number: number;
    status: QuotationStatus;
    quotation_date: string;
    valid_until?: string | null;
    currency: string;
    customer_name: string;
    subtotal_minor_units: number;
    discount_minor_units: number;
    taxable_amount_minor_units: number;
    tax_type?: QuotationTaxType;
    tax_rate_basis_points: number;
    tax_amount_minor_units: number;
    customer_gstin?: string | null;
    place_of_supply_state_code?: string | null;
    place_of_supply_state?: string | null;
    gst_supply_type?: GSTSupplyType | null;
    cgst_minor_units?: number;
    sgst_minor_units?: number;
    igst_minor_units?: number;
    total_amount_minor_units: number;
    payment_collection_type: "none" | "full" | "advance";
    payment_requested_minor_units: number;
    advance_calculation_type?: "fixed" | "percentage" | null;
    advance_percentage_basis_points?: number | null;
    amount_paid_minor_units: number;
    notes?: string | null;
    terms?: string | null;
    items: QuotationItem[];
  };
  supplier: {
    name?: string | null;
    business_address?: string | null;
    state?: string | null;
    state_code?: string | null;
    gstin?: string | null;
    email?: string | null;
    phone?: string | null;
  };
  payment_url?: string | null;
  customer_response?: QuotationCustomerResponse | null;
  pdf_url: string;
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : "The quotation could not be loaded";
}

function day(value?: string | null): string {
  if (!value) return "—";
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? value
    : new Intl.DateTimeFormat("en-IN", { dateStyle: "medium" }).format(parsed);
}

export function PublicQuotationPage() {
  const { shareToken = "" } = useParams();
  const queryClient = useQueryClient();
  const [note, setNote] = useState("");
  const [notice, setNotice] = useState("");
  const queryKey = ["public-quotation", shareToken];
  const query = useQuery({
    queryKey,
    queryFn: () => apiRequest<PublicQuotationEnvelope>(`/api/public/quotations/${encodeURIComponent(shareToken)}`),
    enabled: Boolean(shareToken),
    retry: false,
  });
  const responseMutation = useMutation({
    mutationFn: (response: "accept" | "reject") => apiRequest<PublicQuotationEnvelope>(
      `/api/public/quotations/${encodeURIComponent(shareToken)}/${response}`,
      { method: "POST", body: JSON.stringify({ note: note.trim() || null }) },
    ),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKey, next);
      setNotice(next.customer_response?.response === "accepted" ? "Thank you. Your acceptance has been recorded." : "Your response has been recorded.");
    },
    onError: (error) => setNotice(errorText(error)),
  });
  const downloadMutation = useMutation({
    mutationFn: () => {
      if (!query.data) throw new Error("Quotation is not ready");
      return apiDownload(query.data.pdf_url);
    },
    onSuccess: (blob) => {
      const quotation = query.data?.quotation;
      if (!quotation) return;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `Quotation-${quotation.quotation_number.replaceAll("/", "-")}-R${quotation.revision_number}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    },
    onError: (error) => setNotice(errorText(error)),
  });

  if (query.isPending) return <main className="public-quote-shell"><div className="public-quote-state">Loading your quotation…</div></main>;
  if (query.isError || !query.data) return <main className="public-quote-shell"><div className="public-quote-state error"><h1>Quotation unavailable</h1><p>{errorText(query.error)}</p></div></main>;

  const { quotation, supplier, payment_url: paymentUrl, customer_response: response } = query.data;
  const canRespond = !response && ["ISSUED", "SENT"].includes(quotation.status);
  const canPay = Boolean(paymentUrl && !["REJECTED", "CANCELLED", "EXPIRED", "PAID"].includes(quotation.status));

  return (
    <main className="public-quote-shell">
      <article className="public-quote-card">
        <header className="public-quote-header">
          <div>
            <span>Quotation from</span>
            <h1>{supplier.name || "Your supplier"}</h1>
            <p>{[supplier.business_address, supplier.state ? `${supplier.state}${supplier.state_code ? ` (${supplier.state_code})` : ""}` : null].filter(Boolean).join(", ")}</p>
          </div>
          <div className="public-quote-number">
            <span>Quotation</span>
            <strong>{quotation.quotation_number}</strong>
            <small>Revision {quotation.revision_number}</small>
          </div>
        </header>

        {notice ? <div className="public-quote-notice" role="status">{notice}</div> : null}
        {response ? <div className={`public-quote-decision ${response.response}`}><strong>{response.response === "accepted" ? "Quotation accepted" : "Quotation declined"}</strong><span>Your response was recorded and shared with the sales team.</span></div> : null}

        <section className="public-quote-meta">
          <div><span>Prepared for</span><strong>{quotation.customer_name}</strong></div>
          <div><span>Quotation date</span><strong>{day(quotation.quotation_date)}</strong></div>
          <div><span>Valid until</span><strong>{day(quotation.valid_until)}</strong></div>
          <div><span>Status</span><strong>{quotation.status.replaceAll("_", " ")}</strong></div>
          {quotation.tax_type === "gst" && quotation.place_of_supply_state ? <div><span>Place of supply</span><strong>{quotation.place_of_supply_state} ({quotation.place_of_supply_state_code})</strong></div> : null}
          {quotation.tax_type === "gst" && quotation.customer_gstin ? <div><span>Customer GSTIN</span><strong>{quotation.customer_gstin}</strong></div> : null}
        </section>

        <section className="public-quote-items">
          <div className="public-quote-item heading"><span>Item / service</span><span>Qty</span><span>Rate</span><span>Amount</span></div>
          {quotation.items.map((item) => <div className="public-quote-item" key={item.id}>
            <div><strong>{item.name}</strong>{item.description ? <small>{item.description}</small> : null}</div>
            <span>{quantityFromMilliunits(item.quantity_milliunits)} {item.unit_label || ""}</span>
            <span>{formatMoney(item.unit_amount_minor_units, quotation.currency)}</span>
            <strong>{formatMoney(item.line_subtotal_minor_units, quotation.currency)}</strong>
          </div>)}
        </section>

        <div className="public-quote-lower">
          <section className="public-quote-copy">
            {quotation.notes ? <><h2>Notes</h2><p>{quotation.notes}</p></> : null}
            {quotation.terms ? <><h2>Terms and conditions</h2><p>{quotation.terms}</p></> : null}
            {quotation.tax_type === "gst" ? <p className="public-quote-gst-note">This document is a quotation and not a tax invoice.</p> : null}
          </section>
          <section className="public-quote-totals">
            <div><span>Subtotal</span><strong>{formatMoney(quotation.subtotal_minor_units, quotation.currency)}</strong></div>
            <div><span>Discount</span><strong>- {formatMoney(quotation.discount_minor_units, quotation.currency)}</strong></div>
            {quotation.tax_type === "gst" ? <div><span>Taxable value</span><strong>{formatMoney(quotation.taxable_amount_minor_units, quotation.currency)}</strong></div> : null}
            {quotation.tax_type === "gst" && quotation.gst_supply_type === "intra_state" ? <>
              <div><span>CGST ({Number(quotation.tax_rate_basis_points || 0) / 200}%)</span><strong>{formatMoney(quotation.cgst_minor_units, quotation.currency)}</strong></div>
              <div><span>SGST ({Number(quotation.tax_rate_basis_points || 0) / 200}%)</span><strong>{formatMoney(quotation.sgst_minor_units, quotation.currency)}</strong></div>
            </> : null}
            {quotation.tax_type === "gst" && quotation.gst_supply_type === "inter_state"
              ? <div><span>IGST ({Number(quotation.tax_rate_basis_points || 0) / 100}%)</span><strong>{formatMoney(quotation.igst_minor_units, quotation.currency)}</strong></div>
              : null}
            {quotation.tax_type === "legacy_tax" || (!quotation.tax_type && quotation.tax_amount_minor_units > 0)
              ? <div><span>Tax</span><strong>{formatMoney(quotation.tax_amount_minor_units, quotation.currency)}</strong></div>
              : null}
            <div className="total"><span>Total</span><strong>{formatMoney(quotation.total_amount_minor_units, quotation.currency)}</strong></div>
            {quotation.payment_requested_minor_units > 0 ? <div><span>{quotation.payment_collection_type === "advance" ? `Advance requested${quotation.advance_calculation_type === "percentage" && quotation.advance_percentage_basis_points ? ` (${Number(quotation.advance_percentage_basis_points) / 100}%)` : ""}` : "Payment requested"}</span><strong>{formatMoney(quotation.payment_requested_minor_units, quotation.currency)}</strong></div> : null}
            {quotation.amount_paid_minor_units > 0 ? <div><span>Paid</span><strong>{formatMoney(quotation.amount_paid_minor_units, quotation.currency)}</strong></div> : null}
          </section>
        </div>

        <section className="public-quote-actions">
          <div>
            <Button loading={downloadMutation.isPending} onClick={() => downloadMutation.mutate()}>Download PDF</Button>
            {canPay ? <a className="public-quote-pay" href={paymentUrl || undefined} target="_blank" rel="noreferrer">Pay securely</a> : null}
          </div>
          {canRespond ? <div className="public-quote-response">
            <label><span>Optional note to the sales team</span><textarea maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} /></label>
            <div>
              <Button variant="primary" loading={responseMutation.isPending && responseMutation.variables === "accept"} disabled={responseMutation.isPending} onClick={() => responseMutation.mutate("accept")}>Accept quotation</Button>
              <Button variant="danger" loading={responseMutation.isPending && responseMutation.variables === "reject"} disabled={responseMutation.isPending} onClick={() => responseMutation.mutate("reject")}>Decline</Button>
            </div>
          </div> : null}
        </section>

        <footer>{supplier.email ? <span>{supplier.email}</span> : null}{supplier.phone ? <span>{supplier.phone}</span> : null}{supplier.gstin ? <span>GSTIN {supplier.gstin}</span> : null}</footer>
      </article>
    </main>
  );
}
